import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';

interface SafePerfectScrollbarProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  options?: any;
  onScrollX?: (container: HTMLElement) => void;
  onScrollY?: (container: HTMLElement) => void;
  onScrollUp?: (container: HTMLElement) => void;
  onScrollDown?: (container: HTMLElement) => void;
  onScrollLeft?: (container: HTMLElement) => void;
  onScrollRight?: (container: HTMLElement) => void;
  onYReachStart?: (container: HTMLElement) => void;
  onYReachEnd?: (container: HTMLElement) => void;
  onXReachStart?: (container: HTMLElement) => void;
  onXReachEnd?: (container: HTMLElement) => void;
  containerRef?: (ref: HTMLElement | null) => void;
  id?: string;
}

export interface SafePerfectScrollbarRef {
  updateScroll: () => void;
}

const SafePerfectScrollbar = forwardRef<SafePerfectScrollbarRef, SafePerfectScrollbarProps>(
  ({ children, containerRef, ...props }, ref) => {
    const scrollbarRef = useRef<any>(null);
    const containerElementRef = useRef<HTMLElement | null>(null);
    const isMountedRef = useRef(true);

    useImperativeHandle(ref, () => ({
      updateScroll: () => {
        if (scrollbarRef.current && isMountedRef.current) {
          try {
            scrollbarRef.current.updateScroll();
          } catch (error) {
            // Silently handle update errors when component is unmounting
            console.warn('SafePerfectScrollbar: Error updating scroll:', error);
          }
        }
      },
    }));

    // Safe container ref callback that checks if component is still mounted
    const safeContainerRef = (element: HTMLElement | null) => {
      if (isMountedRef.current) {
        containerElementRef.current = element;
        if (containerRef) {
          containerRef(element);
        }
      }
    };

    // Wrap all scroll event handlers with safety checks
    const createSafeHandler = (handler?: (container: HTMLElement) => void) => {
      if (!handler) return undefined;
      
      return (container: HTMLElement) => {
        if (isMountedRef.current && container && container.classList) {
          try {
            handler(container);
          } catch (error) {
            console.warn('SafePerfectScrollbar: Error in scroll handler:', error);
          }
        }
      };
    };

    const safeProps = {
      ...props,
      onScrollX: createSafeHandler(props.onScrollX),
      onScrollY: createSafeHandler(props.onScrollY),
      onScrollUp: createSafeHandler(props.onScrollUp),
      onScrollDown: createSafeHandler(props.onScrollDown),
      onScrollLeft: createSafeHandler(props.onScrollLeft),
      onScrollRight: createSafeHandler(props.onScrollRight),
      onYReachStart: createSafeHandler(props.onYReachStart),
      onYReachEnd: createSafeHandler(props.onYReachEnd),
      onXReachStart: createSafeHandler(props.onXReachStart),
      onXReachEnd: createSafeHandler(props.onXReachEnd),
      containerRef: safeContainerRef,
    };

    useEffect(() => {
      isMountedRef.current = true;
      
      return () => {
        isMountedRef.current = false;
        // Give a small delay to ensure any pending handlers can check the mounted state
        setTimeout(() => {
          if (containerElementRef.current) {
            containerElementRef.current = null;
          }
        }, 0);
      };
    }, []);

    return (
      <PerfectScrollbar
        ref={scrollbarRef}
        {...safeProps}
      >
        {children}
      </PerfectScrollbar>
    );
  }
);

SafePerfectScrollbar.displayName = 'SafePerfectScrollbar';

export default SafePerfectScrollbar; 